import React from 'react';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { profileMessages as m } from '../profileMessages';
import { ThemeSwatches } from './ThemeSwatches';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileLookStripProps {
  profile: ArtistProfileResponse;
  colours: ThemeColours;
}

/**
 * "Your artist look" (design a1, d1): the theme and the colours that new profile images use.
 * "Change" opens the release that the look was saved from (its Theme panel saves the default),
 * else the newest release.
 */
const ProfileLookStripComponent: React.FC<ProfileLookStripProps> = ({ profile, colours }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const narrow = useIsNarrowScreen();
  const { look } = profile;
  const target = look.savedFrom?.catalogItemUuid ?? profile.newestRelease?.catalogItemUuid;
  const line =
    look.savedFrom === null
      ? intl.formatMessage(m.lookNoRelease)
      : intl.formatMessage(m.lookSavedFrom, { title: look.savedFrom.title });

  return (
    <div className={styles.look} data-testid="profile-look">
      <span className={styles.eyebrow}>{intl.formatMessage(narrow ? m.lookLabelNarrow : m.lookLabel)}</span>
      <b>{intl.formatMessage(releaseThemeMessages[look.themeId].name)}</b>
      <ThemeSwatches colours={colours} />
      <span className={`${styles.grow} ${styles.muted}`}>{narrow ? null : line}</span>
      {target !== undefined && (
        <button
          type="button"
          className={styles.link}
          onClick={() => router.push(SMLRoutes.promoteRelease, { catalogItemUuid: target })}
        >
          {intl.formatMessage(m.lookChange)}
        </button>
      )}
    </div>
  );
};

export const ProfileLookStrip = TypedMemo(ProfileLookStripComponent);
